import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  Renderer2,
} from '@angular/core';
import { Annotation, HighlightStyle, Rect } from '@bukio/viewer';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ToastService } from 'shared/ui';

import { EventBusService } from '../../services/event-bus.service';
import { StorageService } from '../../services/storage.service';

import { Book } from '../../models/book.model';

import { Config } from '../../constants/config';
import {
  HighlightColor,
  HIGHLIGHT_COLORS,
} from '../../constants/highlight-colors';

import { getActiveIframes, getIIDFromRange } from '../../utils/buk-viewer';

import { UIStateService } from '../../services/ui-state.service';
import {
  VIEWER_ENVIRONMENT,
  ViewerModuleEnvironment,
} from '../../viewer.module';
import { ReadingGroupsService } from '../../services/reading-groups.service';

export enum HighlightAction {
  Add,
  Remove,
  Change,
}

export enum ContextMenuItem {
  CopyText,
  ShareTwitter,
  ShareFacebook,
  CopyURL,
  MediaOverlay,
  Memo,
  Comment,
  SelectContinuously,
}

const ACTIVE_STYLE_CLASS = 'is-active';

export interface SelectionContext {
  selectedRange: Range;
  canPlayMediaOverlay: boolean;
  url: string;
  annotation?: Annotation;
  isAtPageBoundary: boolean;
}

enum MenuType {
  TextSelection,
  Highlight,
  Share,
  Colors,
  Commentator,
  ContinueSelecting,
}

function rgbaToWhiteBackground(rgbaColor: string): string {
  const rgba = rgbaColor.slice(5, -1).split(',').map(Number);
  const white = [255, 255, 255];

  const result = [];
  for (let i = 0; i < 3; i++) {
    result.push(Math.round((1 - rgba[3]) * white[i] + rgba[3] * rgba[i]));
  }

  return 'rgb(' + result.join(', ') + ')';
}

@Component({
  selector: 'viewer-context-menu',
  templateUrl: './context-menu.component.html',
  styleUrls: ['./context-menu.component.scss'],
})
export class ContextMenuComponent implements OnInit {
  private unsubscriber: Subject<void> = new Subject<void>();

  public _defaultHighlightStyles!: HighlightStyle[];
  public _additionalHighlightStyles!: HighlightStyle[];

  public _book?: Book;

  public _context?: SelectionContext;
  public _canShareURL = false;

  private _menuSpacing = 10;
  public _type = MenuType.TextSelection;
  public _prevType?: MenuType;

  public readonly _MenuItem = ContextMenuItem;
  public readonly _MenuType = MenuType;

  constructor(
    private _element: ElementRef,
    private _renderer: Renderer2,
    private _eventBusService: EventBusService,
    private _storageService: StorageService,
    private _toastService: ToastService,
    private _uiStateService: UIStateService,
    private _readingGroupsService: ReadingGroupsService,
    @Inject(VIEWER_ENVIRONMENT) private _environment: ViewerModuleEnvironment
  ) {}

  private _isIOS(): boolean {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }

  ngOnInit(): void {
    // if (!this._environment.isApp && CrossBrowsing.touchDevice) {
    //   this._renderer.addClass(this._element.nativeElement, 'mobile');
    // }

    this._eventBusService
      .on('ContentsComponent:highlightClick')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(({ context, offset }) => {
        this.init(context);

        setTimeout(() => {
          this.updatePositionByOffsetAndRange(
            offset.x,
            offset.y,
            context.selectedRange
          );
          this.show();
        });
      });

    this._eventBusService
      .on('ContentsComponent:select')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(({ context, rects }) => {
        this.init(context);

        setTimeout(() => {
          this.updatePositionByRects(
            rects,
            context.selectedRange.startContainer.ownerDocument?.defaultView
              ?.frameElement ?? undefined
          );
          this.show();
        });
      });

    this._eventBusService
      .on('ContentsComponent:deselect')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(() => {
        this.hide();
      });

    this._eventBusService
      .on('ContentsComponent:bookLoad')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(({ book }) => {
        this._book = book;
      });

    // 하이라이트 제거 이벤트 구독
    this._eventBusService
      .on('ReadingGroupMemoListComponent:clearTemporaryHighlight')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(() => {
        this.clearTemporaryHighlight();
      });

    // dot 클릭 시 임시 하이라이트 제거 이벤트 구독
    this._eventBusService
      .on('ContentsComponent:memoIndicatorDotClick')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(() => {
        this.clearTemporaryHighlight();
      });

    // 주소 변경 시 컨텍스트 메뉴 숨김
    this._eventBusService
      .on('ContentsComponent:addressChange')
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(() => {
        this.hide();
      });
  }

  get isReadingGroup(): boolean {
    return this._readingGroupsService.currentGroup !== null;
  }

  show(): void {
    this._setActive(true);
  }

  hide(): void {
    this._setActive(false);
    // 임시 하이라이트는 여기서 제거하지 않음
    // 제거 시점: 1) 댓글 저장 시, 2) 다른 텍스트 선택 시작 시
  }

  private _getInitialMenuType(context: SelectionContext): MenuType {
    return this._uiStateService.isCommentaryEditor
      ? MenuType.Commentator
      : context.annotation
      ? MenuType.Highlight
      : MenuType.TextSelection;
  }

  init(context: SelectionContext): void {
    this._context = context;

    if (context.isAtPageBoundary) {
      this._type = MenuType.ContinueSelecting;
      this._prevType = this._getInitialMenuType(context);
    } else {
      this._type = this._getInitialMenuType(context);
    }

    const rangeIID = getIIDFromRange(context.selectedRange);
    this._canShareURL = this._book!.canShareItem(rangeIID);

    const defaultHighlightColors =
      this._storageService.loadMiscSettings().highlightColors;

    this._defaultHighlightStyles = [];
    this._additionalHighlightStyles = [];

    Object.entries(HIGHLIGHT_COLORS).forEach(([key, color]) => {
      if (defaultHighlightColors.indexOf(key as HighlightColor) !== -1) {
        this._defaultHighlightStyles.push({
          styleClass: key,
          style: `color: ${rgbaToWhiteBackground(color)}`,
        });
      } else {
        this._additionalHighlightStyles.push({
          styleClass: key,
          style: `color: ${rgbaToWhiteBackground(color)}`,
        });
      }
    });
  }

  private _setActive(isActive: boolean): void {
    this._setActiveStyleClass(isActive);
    this._eventBusService.fire('ContextMenuComponent:visibilityChange', {
      isVisible: isActive,
    });
  }

  private _setActiveStyleClass(isActive: boolean): void {
    if (isActive) {
      this._renderer.addClass(this._element.nativeElement, ACTIVE_STYLE_CLASS);
    } else {
      this._renderer.removeClass(
        this._element.nativeElement,
        ACTIVE_STYLE_CLASS
      );
    }
  }

  // 하위 메뉴로 변경 시 컨텍스트 메뉴 너비가 달라지는데 이 때 위치를 다시 계산하지 않도록
  // translateX(-50%) 사용하기 때문에 left나 right가 center로 오게 계산해야함
  private _updatePosition(
    centerFromLeft: number,
    selectionTop: number,
    selectionBottom?: number
  ): void {
    let centerFromRight, bottom, top;

    const menuRect = this._element.nativeElement.getBoundingClientRect();
    const left = centerFromLeft - menuRect.width / 2;

    if (left < this._menuSpacing) {
      centerFromLeft = this._menuSpacing + menuRect.width / 2;
      centerFromRight = undefined;
    } else if (left + menuRect.width + this._menuSpacing > window.innerWidth) {
      centerFromLeft = undefined as any;
      centerFromRight = this._menuSpacing - menuRect.width / 2;
    }

    // iOS에서만 시스템 컨텍스트 메뉴와 반대편에 배치
    if (this._isIOS()) {
      // A: 드래그한 텍스트 영역의 상단 y좌표와 화면 최상단과의 거리
      const selectionTopY = selectionTop;
      const A = selectionTopY;

      // B: 드래그한 텍스트 영역의 하단 y좌표와 화면 최하단과의 거리
      const selectionBottomY = selectionBottom ?? selectionTop;
      const screenHeight = window.innerHeight;
      const B = screenHeight - selectionBottomY;

      // A > B → context-menu를 아래에 배치
      // A < B → context-menu를 위에 배치
      // A == B → context-menu를 아래에 배치
      const shouldPlaceAbove = A < B;

      if (shouldPlaceAbove) {
        // context-menu를 위에 배치 (선택 영역 위쪽)
        const calculatedTop =
          selectionTop - menuRect.height - this._menuSpacing;
        if (calculatedTop >= this._menuSpacing) {
          top = calculatedTop;
          bottom = undefined;
        } else {
          // 위쪽 공간이 부족하면 아래에 배치
          top = selectionBottom
            ? selectionBottom + this._menuSpacing
            : selectionTop + this._menuSpacing;
          bottom = undefined;
        }
      } else {
        // context-menu를 아래에 배치 (선택 영역 아래쪽)
        top = selectionBottom
          ? selectionBottom + this._menuSpacing
          : selectionTop + this._menuSpacing;
        bottom = undefined;

        // 아래쪽 공간이 부족하면 위에 배치
        if (top + menuRect.height + this._menuSpacing > window.innerHeight) {
          top = selectionTop - menuRect.height - this._menuSpacing;
          bottom = undefined;
          // 위쪽도 공간이 부족하면 강제로 아래에 배치
          if (top < this._menuSpacing) {
            top = undefined as any;
            bottom = this._menuSpacing;
          }
        }
      }

      // 최종 경계 체크
      if (top !== undefined && top < this._menuSpacing) {
        top = this._menuSpacing;
        bottom = undefined;
      } else if (
        top !== undefined &&
        top + menuRect.height + this._menuSpacing > window.innerHeight
      ) {
        top = undefined as any;
        bottom = this._menuSpacing;
      }
    } else {
      // iOS가 아닌 경우 기존 로직 사용
      top = selectionBottom
        ? selectionBottom + this._menuSpacing
        : selectionTop + this._menuSpacing;

      if (top < this._menuSpacing) {
        top = this._menuSpacing;
        bottom = undefined;
      } else if (
        top + menuRect.height + this._menuSpacing >
        window.innerHeight
      ) {
        top = undefined as any;
        bottom = this._menuSpacing;
      }
    }

    Object.entries({
      top,
      right: centerFromRight,
      bottom,
      left: centerFromLeft,
    }).forEach(([key, value]) => {
      if (value != null) {
        this._renderer.setStyle(this._element.nativeElement, key, value + 'px');
      } else {
        this._renderer.removeStyle(this._element.nativeElement, key);
      }
    });
  }

  updatePositionByOffsetAndRange(
    offsetX: number,
    offsetY: number,
    range: Range
  ): void {
    const rangeRects = Array.from(range.getClientRects());
    const selectionTop = Math.min(...rangeRects.map((rect) => rect.top));
    const selectionBottom = Math.max(...rangeRects.map((rect) => rect.bottom));

    this._updatePosition(offsetX, selectionTop, selectionBottom);
  }

  updatePositionByRects(rects: Rect[], rectFrame?: Element): void {
    const pageLeft = rectFrame ? rectFrame.getBoundingClientRect().left : 0;
    const pageRight = rectFrame ? rectFrame.getBoundingClientRect().right : 0;

    const rectsInViewport = rects.filter((rect) => {
      return rect.x > pageLeft && rect.x <= pageRight;
    });

    const x = Math.min(...rectsInViewport.map((rect) => rect.x));
    const y = Math.min(...rectsInViewport.map((rect) => rect.y));

    const right = Math.max(
      ...rectsInViewport.map((rect) => rect.x + rect.width)
    );
    const bottom = Math.max(
      ...rectsInViewport.map((rect) => rect.y + rect.height)
    );

    const centerX = x + (right - x) / 2;

    this._updatePosition(centerX, y, bottom);
  }

  _onCommentaryHighlightClick(): void {
    this._onHighlightClick(HighlightColor.Yellow);
  }

  _onHighlightClick(styleClass: string): void {
    if (!this._context) {
      return;
    }

    if (!this._checkTextLength()) {
      return;
    }

    let action: HighlightAction;

    if (!this._context.annotation) {
      action = HighlightAction.Add;
    } else if (this._context.annotation.styleClass !== styleClass) {
      action = HighlightAction.Change;
    } else {
      this.hide();
      return;
    }

    this._eventBusService.fire('ContextMenuComponent:highlight', {
      action,
      context: this._context,
      styleClass,
    });

    this.hide();
  }

  // 함께읽기 모드에서 밑줄 클릭 시 사용할 메서드
  _onReadingGroupUnderlineClick(): void {
    if (!this._context) {
      return;
    }

    if (!this._checkTextLength()) {
      return;
    }

    // 함께읽기 전용 스타일 클래스 사용 (서버에서 실제로 사용하는 클래스명)
    const readingGroupUnderlineStyleClass = 'bukh-color-reading-g';

    let action: HighlightAction;
    if (!this._context.annotation) {
      action = HighlightAction.Add;
    } else if (
      this._context.annotation.styleClass !== readingGroupUnderlineStyleClass
    ) {
      action = HighlightAction.Change;
    } else {
      this.hide();
      return;
    }

    this._eventBusService.fire('ContextMenuComponent:highlight', {
      action,
      context: this._context,
      styleClass: readingGroupUnderlineStyleClass,
    });

    this.hide();
  }

  _onRemoveClick(): void {
    if (!this._context) {
      return;
    }

    this._eventBusService.fire('ContextMenuComponent:highlight', {
      action: HighlightAction.Remove,
      context: this._context,
    });

    this.hide();
  }

  _onMenuClick(menu: ContextMenuItem): void {
    if (!this._context) {
      console.warn('⚠️ No context available');
      return;
    }

    if (!this._checkTextLength()) {
      return;
    }

    // 댓글 클릭 시 선택 범위 보존 및 하이라이트
    if (menu === ContextMenuItem.Comment) {
      this._removeTemporaryHighlight();
    }

    this._eventBusService.fire('ContextMenuComponent:menuClick', {
      menu,
      context: this._context,
    });

    this.hide();
  }

  private _checkTextLength(): boolean {
    if (
      !this._context ||
      this._context.selectedRange.toString().length <=
        Config.selectionLimitLength
    ) {
      return true;
    }

    this._toastService.openWarning('문구의 선택은 500자 이내로 제한합니다.');

    return false;
  }

  private _removeTemporaryHighlight(): void {
    const currentElement: HTMLElement | null =
      this._element.nativeElement.parentElement;

    if (!currentElement) {
      return;
    }

    const activeIframes = getActiveIframes(currentElement);

    activeIframes.forEach((iframe) => {
      const doc = iframe.contentDocument;
      if (!doc) {
        return;
      }

      const allTempHighlights = doc.querySelectorAll(
        '.buk-temp-comment-highlight'
      );

      allTempHighlights.forEach((element) => {
        try {
          const parent = element.parentNode;
          if (parent) {
            while (element.firstChild) {
              parent.insertBefore(element.firstChild, element);
            }
            parent.removeChild(element);
          }
        } catch (error) {
          console.warn('Failed to remove orphaned temporary highlight:', error);
        }
      });
    });
  }

  // 하이라이트 제거를 위한 공개 메서드 (댓글 저장 시 호출)
  public clearTemporaryHighlight(): void {
    this._removeTemporaryHighlight();
  }
}
